import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Container,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { MainLayout } from '../components/Layout';

// Import shared dashboard components
import {
  DashboardHeader,
  DashboardTabs,
  DEFAULT_DASHBOARD_TABS,
} from '../components/dashboard/common';

// Import tab content components
import UserStatisticsTab from '../components/dashboard/UserStatisticsTab';
import EngagementTab from '../components/dashboard/EngagementTab';
import MachineUsageTab from '../components/dashboard/MachineUsageTab';
import ProjectsTab from '../components/dashboard/ProjectsTab';
import MISDataTab from '../components/dashboard/MISDataTab';
import LiveSimulationTab from '../components/dashboard/LiveSimulationTab';
import TLIndividualLiveFeed from '../components/dashboard/TLIndividualLiveFeed';

const TLDashboard = ({ collapsed, setCollapsed }) => {
  const { user } = useSelector((state) => state.user.user);
  const [lastUpdated, setLastUpdated] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('Weekly');
  const [activeTab, setActiveTab] = useState('User Statistics');
  const [selectedTL, setSelectedTL] = useState('');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // TODO: Replace with actual TL options from API
  const tlOptions = [
    { id: 'tl1', name: 'TL Lab 1' },
    { id: 'tl2', name: 'TL Lab 2' },
    { id: 'tl3', name: 'TL Lab 3' },
  ];

  useEffect(() => {
    if (isMobile) {
      setCollapsed(true);
    }
  }, [isMobile, setCollapsed]);

  useEffect(() => {
    updateTimestamp();
  }, []);

  const updateTimestamp = () => {
    const now = new Date();
    const options = {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    };
    const formattedDate = now.toLocaleString('en-US', options).replace(',', '');
    setLastUpdated(formattedDate);
  };

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
  };

  const handleRefresh = () => {
    updateTimestamp();
  };

  const handleDownload = () => {
    // TODO: Add download functionality
    console.log('Download clicked');
  };

  const handleTLChange = (tlId) => {
    setSelectedTL(tlId);
    // TODO: Fetch data for selected TL
    console.log('Selected TL:', tlId);
  };

  const handleTabChange = (tabName) => {
    setActiveTab(tabName);
  };

  /**
   * Render the active tab content
   */
  const renderTabContent = () => {
    const periodLower = selectedPeriod.toLowerCase();

    switch (activeTab) {
      case 'User Statistics':
        return <UserStatisticsTab selectedPeriod={periodLower} />;
      case 'Engagement':
        return <EngagementTab selectedPeriod={periodLower} />;
      case 'Projects':
        return <ProjectsTab selectedPeriod={periodLower} />;
      case 'Machine Usage':
        return (
          <Box>
            <MachineUsageTab selectedPeriod={periodLower} />
          </Box>
        );
      case 'MIS Data':
        return <MISDataTab selectedPeriod={periodLower} />;
      case 'Live Feed':
        return <LiveSimulationTab siteId={selectedTL} />;
      default:
        return <UserStatisticsTab selectedPeriod={periodLower} />;
    }
  };

  return (
    <MainLayout collapsed={collapsed} setCollapsed={setCollapsed}>
      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          bgcolor: '#ffffff',
          p: { xs: 1.5, sm: 2, md: 3 },
        }}
      >
        <Container maxWidth="xl" sx={{ px: { xs: 0, sm: 1, md: 2 } }}>
          {/* Dashboard Header with TL Selector */}
          <DashboardHeader
            title="Dashboard"
            lastUpdated={lastUpdated}
            selectedPeriod={selectedPeriod}
            onPeriodChange={handlePeriodChange}
            onRefresh={handleRefresh}
            onDownload={handleDownload}
            selectorLabel="Select TL"
            selectorOptions={tlOptions}
            selectedOption={selectedTL}
            onOptionChange={handleTLChange}
            showSelector={true}
          />

          {/* Dashboard Tabs */}
          <DashboardTabs
            tabs={DEFAULT_DASHBOARD_TABS}
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />

          {/* Tab Content */}
          <Box
            sx={{
              bgcolor: '#ffffff',
              borderRadius: { xs: '8px', md: '12px' },
              padding: { xs: '12px', sm: '16px', md: '24px' },
              boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.05)',
            }}
          >
            {renderTabContent()}
          </Box>
        </Container>
      </Box>
    </MainLayout>
  );
};

export default TLDashboard;
